import type { FaqBlockProps, FaqItem } from '../types';
import { theme, sectionColors } from '../theme';

/** The items worth rendering: both halves present, trimmed. Shared with the FAQPage emitter. */
export function faqItems(items: FaqItem[] | undefined): FaqItem[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((i) => ({ question: String(i?.question ?? '').trim(), answer: String(i?.answer ?? '').trim() }))
    .filter((i) => i.question && i.answer);
}

/**
 * Questions and answers — the answer block. Plain <details> disclosures, so it works with no
 * JavaScript and every answer is in the HTML for a crawler or an assistant to read. The page
 * that renders it also emits the same items as FAQPage structured data (tenant-sites).
 * Renders nothing when there is no complete question-and-answer pair.
 */
export function FaqBlock(props: FaqBlockProps) {
  const { heading, subheading } = props;
  const items = faqItems(props.items);
  const colors = sectionColors(props, { bg: 'transparent', text: theme.text });
  if (items.length === 0) return null;

  return (
    <section className="px-6 py-12" style={{ ...colors, fontFamily: theme.bodyFont }} data-block="faq">
      <div className="mx-auto max-w-3xl">
        {heading ? (
          <h2 className="text-center text-2xl font-semibold" style={{ fontFamily: theme.headingFont }}>
            {heading}
          </h2>
        ) : null}
        {subheading ? <p className="mt-2 text-center text-base opacity-75">{subheading}</p> : null}
        <div className={`${heading || subheading ? 'mt-8 ' : ''}divide-y divide-black/10 border-y border-black/10`}>
          {items.map((item, i) => (
            <details key={i} className="group py-4" open={i === 0}>
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold [&::-webkit-details-marker]:hidden">
                <span>{item.question}</span>
                <span aria-hidden="true" className="mt-0.5 shrink-0 text-lg leading-none opacity-60 transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <div className="mt-3 max-w-prose text-sm leading-relaxed opacity-85">
                {item.answer.split(/\n{2,}/).map((para, j) => (
                  <p key={j} className={j > 0 ? 'mt-3' : undefined}>
                    {para}
                  </p>
                ))}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
